/**
 * Spanish A1 Conti Sub-Units (MARSEARS Framework)
 * Each sub-unit follows Conti's M-A-R-S-E-A-R-S pipeline
 * with systematic recycling across the matrix.
 */

import type { ContiUnit, SubUnit } from '@/types/language';
import { SPANISH_A1_SENTENCE_BUILDERS } from './sentence-builders-a1';
import { SPANISH_A1_NARROW_TEXTS } from './narrow-texts-a1';

const subUnit01: SubUnit = {
  id: 'sub-es-a1-01',
  title: 'Presentarse y Saludar',
  order: 1,
  newChunkIds: ['es-a1-001', 'es-a1-002', 'es-a1-004', 'es-a1-005', 'es-a1-006', 'es-a1-007', 'es-a1-008', 'es-a1-012', 'es-a1-075'],
  recycledChunkIds: [],
  phases: ['modelling', 'awareness_raising', 'receptive_processing', 'structured_production'],
  activities: {
    modelling: [
      {
        type: 'sentence_builder',
        title: 'Presentacion guiada',
        instructions: 'Construye oraciones usando el Sentence Builder',
        columns: SPANISH_A1_SENTENCE_BUILDERS[0].columns,
        targetSentences: ['Hola me llamo Juan', 'Buenos dias soy Maria'],
        languageCode: 'es',
        order: 1,
        id: 'act-es-a1-01-m1',
        pedagogicalFocus: ['Greetings', 'Self-introduction'],
      },
    ],
    awareness_raising: [
      {
        type: 'multiple_choice',
        title: 'Ser vs Estar basico',
        instructions: 'Elige la opcion correcta',
        question: 'Completa: Yo ___ estudiante.',
        options: ['soy', 'estoy', 'es', 'esta'],
        correctIndex: 0,
        explanation: 'Ser se usa para caracteristicas permanentes como profesion',
        order: 2,
        id: 'act-es-a1-01-a1',
        pedagogicalFocus: ['Ser vs Estar', 'Permanent characteristics'],
      },
    ],
    receptive_processing: [
      {
        type: 'narrow_reading',
        title: 'Lectura: Mi Presentacion',
        instructions: 'Lee el texto y encuentra las frases clave',
        body: SPANISH_A1_NARROW_TEXTS[0].body,
        translation: SPANISH_A1_NARROW_TEXTS[0].translation,
        comprehensionQuestions: [
          {
            type: 'multiple_choice',
            question: 'De donde es Carlos?',
            options: ['De Barcelona', 'De Madrid', 'De Mexico', 'De Argentina'],
            correctIndex: 1,
          },
        ],
        targetChunks: SPANISH_A1_NARROW_TEXTS[0].targetChunks,
        order: 3,
        id: 'act-es-a1-01-r1',
        pedagogicalFocus: ['Comprehensible input', 'Chunk recognition'],
      },
    ],
    structured_production: [
      {
        type: 'translation_drill',
        title: 'Traduccion: Presentacion',
        instructions: 'Traduce las frases al espanol',
        direction: 'from_target',
        items: [
          { source: 'Hello, my name is Juan.', target: 'Hola, me llamo Juan.' },
          { source: 'I am from Spain.', target: 'Soy de Espana.' },
          { source: 'I am a student.', target: 'Soy estudiante.' },
        ],
        order: 4,
        id: 'act-es-a1-01-s1',
        pedagogicalFocus: ['Scaffolded output', 'Active recall'],
      },
    ],
    expansion: [],
    autonomy_assessment: [],
    retrieval_recycling: [],
    spontaneity: [],
  },
  sentenceBuilders: [SPANISH_A1_SENTENCE_BUILDERS[0]],
  narrowTexts: [SPANISH_A1_NARROW_TEXTS[0]],
  estimatedMinutes: 25,
};

const subUnit02: SubUnit = {
  id: 'sub-es-a1-02',
  title: 'Describir Personas y Estados',
  order: 2,
  newChunkIds: ['es-a1-039', 'es-a1-040', 'es-a1-049', 'es-a1-050', 'es-a1-108', 'es-a1-110', 'es-a1-112', 'es-a1-113'],
  recycledChunkIds: ['es-a1-006', 'es-a1-007', 'es-a1-001', 'es-a1-005'],
  phases: ['modelling', 'awareness_raising', 'receptive_processing', 'structured_production', 'retrieval_recycling'],
  activities: {
    modelling: [
      {
        type: 'sentence_builder',
        title: 'Describir con Ser y Estar',
        instructions: 'Construye descripciones usando las estructuras',
        columns: SPANISH_A1_SENTENCE_BUILDERS[1].columns,
        targetSentences: ['Yo soy alto', 'Yo estoy cansado'],
        languageCode: 'es',
        order: 1,
        id: 'act-es-a1-02-m1',
        pedagogicalFocus: ['Description', 'Ser vs Estar', 'Adjective agreement'],
      },
    ],
    awareness_raising: [
      {
        type: 'multiple_choice',
        title: 'Genero de adjetivos',
        instructions: 'Elige la forma correcta del adjetivo',
        question: 'La casa es ___',
        options: ['grande', 'grandes', 'granda', 'grandemente'],
        correctIndex: 0,
        explanation: 'Grande es invariable en genero',
        order: 2,
        id: 'act-es-a1-02-a1',
        pedagogicalFocus: ['Adjective gender', 'Noun-adjective agreement'],
      },
    ],
    receptive_processing: [
      {
        type: 'narrow_reading',
        title: 'Lectura: Mi Familia',
        instructions: 'Lee sobre la familia y encuentra los adjetivos',
        body: SPANISH_A1_NARROW_TEXTS[2].body,
        translation: SPANISH_A1_NARROW_TEXTS[2].translation,
        comprehensionQuestions: [
          {
            type: 'multiple_choice',
            question: 'Como es la madre?',
            options: ['Alta', 'Amable', 'Pequena', 'Bonita'],
            correctIndex: 1,
          },
        ],
        targetChunks: SPANISH_A1_NARROW_TEXTS[2].targetChunks,
        order: 3,
        id: 'act-es-a1-02-r1',
        pedagogicalFocus: ['Adjective identification', 'Family vocabulary'],
      },
    ],
    structured_production: [
      {
        type: 'pattern_substitution',
        title: 'Sustitucion de adjetivos',
        instructions: 'Cambia los adjetivos en las oraciones',
        sentenceTemplate: 'El chico es SUB',
        substitutionSlot: 'SUB',
        substitutions: [
          { chunk: 'alto', translation: 'tall' },
          { chunk: 'bajo', translation: 'short' },
          { chunk: 'amable', translation: 'kind' },
        ],
        languageCode: 'es',
        order: 4,
        id: 'act-es-a1-02-s1',
        pedagogicalFocus: ['Pattern variation', 'Adjective substitution'],
      },
    ],
    expansion: [],
    autonomy_assessment: [],
    retrieval_recycling: [
      {
        type: 'interleaved_review',
        title: 'Repaso: Presentacion + Descripcion',
        instructions: 'Repasa las frases de la unidad 1 y 2',
        items: [
          { question: 'Como dices "Hello, my name is Juan"?', correctAnswer: 'Hola, me llamo Juan.', type: 'translation' },
          { question: 'Como dices "I am tall"?', correctAnswer: 'Soy alto.', type: 'translation' },
        ],
        vocabularyIds: ['es-a1-001', 'es-a1-006', 'es-a1-007', 'es-a1-012'],
        lessonIds: ['sub-es-a1-01', 'sub-es-a1-02'],
        order: 5,
        id: 'act-es-a1-02-rr1',
        pedagogicalFocus: ['Interleaving', 'Retrieval practice', 'Spaced repetition'],
      },
    ],
    spontaneity: [],
  },
  sentenceBuilders: [SPANISH_A1_SENTENCE_BUILDERS[1]],
  narrowTexts: [SPANISH_A1_NARROW_TEXTS[2]],
  estimatedMinutes: 30,
};

const subUnit03: SubUnit = {
  id: 'sub-es-a1-03',
  title: 'Gustos y Preferencias',
  order: 3,
  newChunkIds: ['es-a1-028', 'es-a1-029', 'es-a1-030', 'es-a1-037', 'es-a1-038', 'es-a1-085', 'es-a1-087'],
  recycledChunkIds: ['es-a1-006', 'es-a1-007', 'es-a1-012', 'es-a1-039', 'es-a1-049', 'es-a1-075'],
  phases: ['modelling', 'receptive_processing', 'structured_production', 'retrieval_recycling'],
  activities: {
    modelling: [
      {
        type: 'sentence_builder',
        title: 'Expresar gustos',
        instructions: 'Construye frases sobre gustos',
        columns: SPANISH_A1_SENTENCE_BUILDERS[2].columns,
        targetSentences: ['Me gusta el cafe', 'Me gusta mucho la musica'],
        languageCode: 'es',
        order: 1,
        id: 'act-es-a1-03-m1',
        pedagogicalFocus: ['Gustar structure', 'Likes and dislikes'],
      },
    ],
    receptive_processing: [
      {
        type: 'narrow_reading',
        title: 'Lectura: Mis Gustos',
        instructions: 'Lee y encuentra las expresiones de gusto',
        body: SPANISH_A1_NARROW_TEXTS[3].body,
        translation: SPANISH_A1_NARROW_TEXTS[3].translation,
        comprehensionQuestions: [
          {
            type: 'multiple_choice',
            question: 'Que musica le gusta?',
            options: ['Reggaeton', 'Pop y rock', 'Clasica', 'Jazz'],
            correctIndex: 1,
          },
        ],
        targetChunks: SPANISH_A1_NARROW_TEXTS[3].targetChunks,
        order: 2,
        id: 'act-es-a1-03-r1',
        pedagogicalFocus: ['Gustar comprehension', 'Intensifiers'],
      },
    ],
    structured_production: [
      {
        type: 'translation_drill',
        title: 'Traduccion: Gustos',
        instructions: 'Traduce las frases',
        direction: 'from_target',
        items: [
          { source: 'I like coffee.', target: 'Me gusta el cafe.' },
          { source: 'I like it a lot.', target: 'Me gusta mucho.' },
          { source: 'I do not like it at all.', target: 'No me gusta nada.' },
        ],
        order: 3,
        id: 'act-es-a1-03-s1',
        pedagogicalFocus: ['Gustar production', 'Intensifier use'],
      },
    ],
    retrieval_recycling: [
      {
        type: 'interleaved_review',
        title: 'Repaso completo: Unidades 1-3',
        instructions: 'Repasa todo lo aprendido',
        items: [
          { question: 'Como te llamas?', correctAnswer: 'Me llamo...', type: 'production' },
          { question: 'Como dices "I am tired"?', correctAnswer: 'Estoy cansado.', type: 'translation' },
          { question: 'Como dices "I like music"?', correctAnswer: 'Me gusta la musica.', type: 'translation' },
        ],
        vocabularyIds: ['es-a1-001', 'es-a1-006', 'es-a1-007', 'es-a1-012', 'es-a1-028', 'es-a1-039'],
        lessonIds: ['sub-es-a1-01', 'sub-es-a1-02', 'sub-es-a1-03'],
        order: 4,
        id: 'act-es-a1-03-rr1',
        pedagogicalFocus: ['Interleaving', 'Retrieval practice'],
      },
    ],
    spontaneity: [],
    awareness_raising: [],
    expansion: [],
    autonomy_assessment: [],
  },
  sentenceBuilders: [SPANISH_A1_SENTENCE_BUILDERS[2]],
  narrowTexts: [SPANISH_A1_NARROW_TEXTS[3]],
  estimatedMinutes: 30,
};

export const SPANISH_A1_UNITS: ContiUnit[] = [
  {
    id: 'unit-es-a1-01',
    title: 'Introduccion y Presentacion Personal',
    description: 'Aprende a presentarte, describirte a ti mismo y expresar gustos basicos en espanol',
    languageCode: 'es',
    cefrLevel: 'A1',
    subUnits: [subUnit01, subUnit02, subUnit03],
    vocabularyIds: [
      'es-a1-001', 'es-a1-002', 'es-a1-004', 'es-a1-005', 'es-a1-006', 'es-a1-007',
      'es-a1-008', 'es-a1-012', 'es-a1-039', 'es-a1-040', 'es-a1-049', 'es-a1-050',
      'es-a1-075', 'es-a1-108', 'es-a1-110', 'es-a1-112', 'es-a1-113',
      'es-a1-028', 'es-a1-029', 'es-a1-030', 'es-a1-037', 'es-a1-038', 'es-a1-085', 'es-a1-087',
    ],
    functions: ['Greeting', 'Self-introduction', 'Describing people', 'Expressing likes/dislikes'],
    grammarPoints: [
      {
        id: 'gp-es-a1-001',
        title: 'Ser vs Estar',
        explanation: 'Ser se usa para caracteristicas permanentes (profesion, nacionalidad). Estar se usa para estados temporales y ubicacion.',
        examples: [
          { sentence: 'Soy estudiante.', translation: 'I am a student.' },
          { sentence: 'Estoy cansado.', translation: 'I am tired.' },
        ],
        rules: ['Ser: permanent characteristics', 'Estar: temporary states/location'],
        introducePhase: 'awareness_raising',
      },
      {
        id: 'gp-es-a1-002',
        title: 'Gustar structure',
        explanation: 'Gustar requires indirect object pronouns. "Me gusta" + singular noun/infinitive, "Me gustan" + plural noun.',
        examples: [
          { sentence: 'Me gusta el cafe.', translation: 'I like coffee.' },
        ],
        rules: ['Me/te/le/nos/les + gusta + singular noun/infinitive', 'Me/te/le/nos/les + gustan + plural noun'],
        introducePhase: 'expansion',
      },
    ],
  },
];

export default SPANISH_A1_UNITS;
